import type { AggregatedNoteDto } from "../../../services/vorgangNoteService";
import { bloeckeAus, notizSchluessel } from "../../notizbloecke/blockModell";
import { versteckteKommentare } from "../../notizbloecke/kommentarModell";
import type { VerlaufEintrag } from "../v3/verlauf";
import { ereignisseAus } from "./ereignisBloecke";

/**
 * Was in einem Arbeitsstrang steht — gezählt, nicht gezeichnet.
 *
 * ══ WOZU ═══════════════════════════════════════════════════════════════════
 * Seit dem 15.09.2026 ist der Notizbereich wieder geteilt: oben die Notizen,
 * darunter der Verlauf (Mail, Anruf, Dokument, Termin). Ein ZUGEKLAPPTES To-do
 * sagt dann „2 Notizen · 5 im Verlauf" — man sieht, wo etwas liegt, ohne jedes
 * To-do aufzuklappen. Der aufgeklappte Strang nennt dieselben Zahlen über
 * seinen beiden Teilen. Beide Stellen müssen dasselbe zählen, darum steht die
 * Regel hier und nicht zweimal im Render-Baum.
 * ═══════════════════════════════════════════════════════════════════════════
 */

/**
 * Die Ziele der Ereignisse dieses Strangs — `mail:7`, `anruf:8`, `dokument:9`,
 * `termin:4`. Nur Kommentare an diesen Zielen wandern in die Kommentarspalte
 * der Verlaufskarte (`versteckteKommentare`); ein Kommentar an einer Mail, die
 * anderswo hängt, bleibt als Block sichtbar — sonst wäre er nirgends mehr zu
 * sehen.
 *
 * Der TERMIN steht seit dem 20.09.2026 mit in der Menge. Fehlte er hier, käme
 * sein Kommentar zweimal vor: als Block im Notizstrom UND in der Spalte neben
 * der Terminkarte.
 */
export function ereignisZieleAus(eintraege: VerlaufEintrag[]): Set<string> {
    const out = new Set<string>();
    for (const e of eintraege) {
        if (e.mail) out.add(`mail:${e.mail.id}`);
        else if (e.anruf) out.add(`anruf:${e.anruf.id}`);
        else if (e.dokument) out.add(`dokument:${e.dokument.id}`);
        else if (e.termin) out.add(`termin:${e.termin.terminId}`);
    }
    return out;
}

export interface StrangZahlen {
    /** Notizblöcke, wie die Fläche sie zeichnet — ohne versteckte Kommentare, ohne leere. */
    notizen: number;
    /** Einträge des Verlaufs: Mails, Anrufe, Dokumente, Termine. */
    verlauf: number;
}

export function strangZahlen(a: {
    eintraege: VerlaufEintrag[];
    alleNotizen: AggregatedNoteDto[];
    todoId: number | null;
}): StrangZahlen {
    const versteckt = versteckteKommentare(a.alleNotizen, ereignisZieleAus(a.eintraege));
    const sichtbar = versteckt.size
        ? a.alleNotizen.filter((n) => !versteckt.has(notizSchluessel(n)))
        : a.alleNotizen;
    return {
        notizen: bloeckeAus(sichtbar, a.todoId).length,
        verlauf: ereignisseAus(a.eintraege, null).length,
    };
}

/** „1 Notiz · 3 im Verlauf" — leer, wenn der Strang noch nichts trägt. */
export function strangZahlenText(z: StrangZahlen): string {
    if (z.notizen === 0 && z.verlauf === 0) return "";
    return `${z.notizen} ${z.notizen === 1 ? "Notiz" : "Notizen"} · ${z.verlauf} im Verlauf`;
}
